import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserProfile, Study, Award, Organization, StudyMetrics, Course, CourseMetrics } from '../types';
import { Icons } from './ui/Icons';
import { getOrgStudies, getUserStudies, getOrganization, getOrgMembers, updateMemberRole, deleteStudy, adoptStudy, unadoptStudy, getStudyMetrics, getOrgCourses, deleteCourse, getCourseMetrics, getUserQuestions } from '../services/firebase';
import type { Question } from '../types';
import { isStudyUnlockedInCourse, findCourseForStudy } from '../utils/courseUtils';
import { formatAuthorName } from '../utils/formatters';
import { useNavigate } from 'react-router-dom';

import { AppHeader } from './ui/AppHeader';
import { Pagination, getPaginatedItems, getTotalPages, ITEMS_PER_PAGE } from './ui/Pagination';

interface DashboardProps {
  user: UserProfile;
  onLogout: () => void;
  onCreateStudy: () => void;
  onTakeStudy: (study: Study) => void;
  onEditStudy: (study: Study) => void;
  onCreateCourse: () => void;
  onEditCourse: (course: Course) => void;
  onViewCourse: (courseId: string) => void;
  initialActiveTab?: 'browse' | 'profile' | 'members' | 'courses';
  organization?: Organization | null;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onLogout, onCreateStudy, onTakeStudy, onEditStudy, onCreateCourse, onEditCourse, onViewCourse, initialActiveTab, organization: propOrg }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read tab from URL params, fallback to initialActiveTab or 'browse'
  const tabFromUrl = searchParams.get('tab') as 'browse' | 'profile' | 'members' | 'courses' | null;
  const [activeTab, setActiveTab] = useState<'browse' | 'profile' | 'members' | 'courses'>(
    tabFromUrl || initialActiveTab || 'browse'
  );
  const [orgStudies, setOrgStudies] = useState<Study[]>([]);
  const [myStudies, setMyStudies] = useState<Study[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [allCourses, setAllCourses] = useState<Course[]>([]); // All courses for checking study locks
  const [organization, setOrganization] = useState<Organization | null>(propOrg || null);
  const [loading, setLoading] = useState(true);

  // Sync organization from props if it changes
  useEffect(() => {
    if (propOrg) {
      setOrganization(propOrg);
    }
  }, [propOrg]);

  // Members State
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Search and Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'recent' | 'oldest'>('recent');
  const [completionFilter, setCompletionFilter] = useState<'all' | 'completed' | 'not-completed' | 'all-including-locked'>('all');

  // Locked Study Modal State
  const [lockedStudyModal, setLockedStudyModal] = useState<{ study: Study; course: Course } | null>(null);
  const [showOnlyMyStudies, setShowOnlyMyStudies] = useState(false);

  // Pagination State
  const [studiesPage, setStudiesPage] = useState(1);
  const [coursesPage, setCoursesPage] = useState(1);
  const [membersPage, setMembersPage] = useState(1);
  const [completedStudiesPage, setCompletedStudiesPage] = useState(1);

  // Delete Confirmation State
  const [studyToDelete, setStudyToDelete] = useState<Study | null>(null);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);

  // Preview State
  const [previewStudy, setPreviewStudy] = useState<Study | null>(null);

  // Metrics State
  const [studyMetrics, setStudyMetrics] = useState<Record<string, StudyMetrics>>({});
  const [courseMetrics, setCourseMetrics] = useState<Record<string, CourseMetrics>>({});

  // User Questions State
  const [userQuestions, setUserQuestions] = useState<Question[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Hero Carousel State (for when no course is in progress)
  const [heroCarouselIndex, setHeroCarouselIndex] = useState(0);

  // Sync activeTab with URL params when they change
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!user.orgId) return; // Should handle this higher up, but safety check

        const [orgData, studies, coursesData] = await Promise.all([
          getOrganization(user.orgId),
          getOrgStudies(user.orgId),
          getOrgCourses(user.orgId)
        ]);

        setOrganization(orgData);
        setOrgStudies(studies);
        setAllCourses(coursesData); // Store all courses for lock checking

        if (user.isAdmin) {
          const myCreated = await getUserStudies(user.uid);
          setMyStudies(myCreated);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user.uid, user.orgId, activeTab, user.isAdmin]);

  // Fetch members when tab is active
  useEffect(() => {
    if (activeTab === 'members' && user.isAdmin && user.orgId) {
      const fetchMembers = async () => {
        setLoadingMembers(true);
        try {
          const orgMembers = await getOrgMembers(user.orgId!);
          setMembers(orgMembers);
        } catch (e) {
          console.error("Failed to load members", e);
        } finally {
          setLoadingMembers(false);
        }
      };
      fetchMembers();
    }
  }, [activeTab, user.isAdmin, user.orgId]);

  // Fetch user questions when profile tab is active
  useEffect(() => {
    if (activeTab === 'profile') {
      const fetchUserQuestions = async () => {
        setLoadingQuestions(true);
        try {
          const questions = await getUserQuestions(user.uid);
          // Sort by most recent first
          questions.sort((a, b) => b.createdAt - a.createdAt);
          setUserQuestions(questions);
        } catch (e) {
          console.error("Failed to load user questions", e);
        } finally {
          setLoadingQuestions(false);
        }
      };
      fetchUserQuestions();
    }
  }, [activeTab, user.uid]);


  // Hero carousel auto-rotation for course discovery
  useEffect(() => {
    // Get courses that haven't been started yet (for discovery carousel)
    const availableCourses = allCourses.filter(course => {
      if (!course.studyIds || course.studyIds.length === 0) return false;
      const completedInCourse = course.studyIds.filter(id => user.completedStudyIds?.includes(id)).length;
      return completedInCourse === 0; // Not started
    });

    // Check if there's an in-progress course
    const hasInProgressCourse = allCourses.some(course => {
      if (!course.studyIds || course.studyIds.length === 0) return false;
      const completedInCourse = course.studyIds.filter(id => user.completedStudyIds?.includes(id)).length;
      return completedInCourse > 0 && completedInCourse < course.studyIds.length;
    });

    // Only run carousel if no course is in progress and there are courses to show
    if (hasInProgressCourse || availableCourses.length <= 1 || activeTab !== 'browse') return;

    const interval = setInterval(() => {
      setHeroCarouselIndex(prev => (prev + 1) % availableCourses.length);
    }, 6000); // Rotate every 6 seconds

    return () => clearInterval(interval);
  }, [allCourses, user.completedStudyIds, activeTab]);

  // Fetch courses when tab is active
  useEffect(() => {
    if (activeTab === 'courses' && user.orgId) {
      const fetchCourses = async () => {
        setLoading(true);
        try {
          const orgCourses = await getOrgCourses(user.orgId!);
          setCourses(orgCourses);

          // Fetch metrics for courses if user is admin
          if (user.isAdmin) {
            const metricsPromises = orgCourses.map(course => getCourseMetrics(course.id));
            const metricsResults = await Promise.all(metricsPromises);
            const metricsMap: Record<string, CourseMetrics> = {};
            metricsResults.forEach((metrics, index) => {
              if (metrics) {
                metricsMap[orgCourses[index].id] = metrics;
              }
            });
            setCourseMetrics(metricsMap);
          }
        } catch (e) {
          console.error("Failed to load courses", e);
        } finally {
          setLoading(false);
        }
      };
      fetchCourses();
    }
  }, [activeTab, user.orgId, user.isAdmin]);

  const handleUpdateRole = async (targetUserId: string, newRoleIsAdmin: boolean) => {
    try {
      await updateMemberRole(targetUserId, newRoleIsAdmin);
      // Update local state
      setMembers(prev => prev.map(m => m.uid === targetUserId ? { ...m, isAdmin: newRoleIsAdmin } : m));
    } catch (e) {
      alert("Failed to update user role");
      console.error(e);
    }
  };

  const executeDeleteStudy = async () => {
    if (!studyToDelete) return;
    try {
      await deleteStudy(studyToDelete.id);
      // Remove from local state to avoid refetch
      setOrgStudies(prev => prev.filter(study => study.id !== studyToDelete.id));
      setMyStudies(prev => prev.filter(study => study.id !== studyToDelete.id));
    } catch (e) {
      console.error("Failed to delete study", e);
      alert("Failed to delete study.");
    } finally {
      setStudyToDelete(null);
    }
  };

  const handleAdoptStudy = async (studyId: string) => {
    if (!user.orgId) return;
    try {
      await adoptStudy(user.orgId, studyId, user.uid, user.displayName || 'Unknown User');
      // Reload org studies to include the newly cloned study
      const studies = await getOrgStudies(user.orgId);
      setOrgStudies(studies);
      alert("Study added to your organization's library!");
    } catch (e) {
      console.error(e);
      alert("Failed to adopt study.");
    }
  };

  const handleUnadoptStudy = async (studyId: string) => {
    if (!user.orgId) return;
    try {
      await unadoptStudy(user.orgId, studyId);
      // Reload org studies to remove the deleted cloned study
      const studies = await getOrgStudies(user.orgId);
      setOrgStudies(studies);
    } catch (e) {
      console.error(e);
      alert("Failed to remove study.");
    }
  };

  // Derived state for filtering
  // When "Created By Me" is toggled, use myStudies (includes all visibility levels)
  // Otherwise use orgStudies (excludes private studies)
  const baseStudies = showOnlyMyStudies && user.isAdmin ? myStudies : orgStudies;

  const uniqueTags = Array.from(new Set<string>(baseStudies.flatMap(study => study.tags || []))).sort();

  const filteredStudies = baseStudies
    .filter(study => {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        study.title.toLowerCase().includes(query) ||
        study.description.toLowerCase().includes(query) ||
        (study.tags && study.tags.some(t => t.toLowerCase().includes(query)));

      // AND logic: Study must contain ALL selected tags
      const matchesTags = selectedTags.length === 0 ||
        (study.tags && selectedTags.every(t => study.tags.includes(t)));

      // Completion filter
      const isCompleted = user.completedStudyIds?.includes(study.id) || false;
      const matchesCompletion =
        completionFilter === 'all' ||
        completionFilter === 'all-including-locked' ||
        (completionFilter === 'completed' && isCompleted) ||
        (completionFilter === 'not-completed' && !isCompleted);

      // Plan A: Filter out studies that are locked in sequential courses
      // A study is accessible if:
      // 1. It's not part of any course with sequential viewing, OR
      // 2. It's unlocked in its course (user has completed prerequisites)
      // 3. OR user selected "all-including-locked" filter
      const course = findCourseForStudy(study.id, allCourses);
      const isAccessible = completionFilter === 'all-including-locked' ||
        !course ||
        !course.requireSequentialViewing ||
        isStudyUnlockedInCourse(study.id, course, user);

      return matchesSearch && matchesTags && matchesCompletion && isAccessible;
    })
    .sort((a, b) => {
      // Sort by date
      if (sortBy === 'recent') {
        return b.createdAt - a.createdAt; // Newest first
      } else {
        return a.createdAt - b.createdAt; // Oldest first
      }
    });

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  // Reset pagination when filters change
  useEffect(() => {
    setStudiesPage(1);
  }, [searchQuery, selectedTags, sortBy, completionFilter, showOnlyMyStudies]);

  const StudyCard: React.FC<{ study: Study, isAuthor: boolean, isAdoptedView?: boolean, isCommunityView?: boolean }> = ({ study, isAuthor, isAdoptedView = false, isCommunityView = false }) => {
    const isCompleted = user.completedStudyIds?.includes(study.id) || false;
    const progressIndex = user.studyProgress?.[study.id] || 0;
    // Cap progress percentage at 100%
    const progressPercent = isCompleted ? 100 : Math.min(100, Math.round((progressIndex / Math.max(study.segments.length, 1)) * 100));

    // Check if this study has been cloned/adopted by our org
    const isAdopted = orgStudies.some(s => s.clonedFrom === study.id);

    // Check if this study is locked (part of a sequential course and not yet unlocked)
    const studyCourse = findCourseForStudy(study.id, allCourses);
    const isLocked = studyCourse &&
      studyCourse.requireSequentialViewing &&
      !isStudyUnlockedInCourse(study.id, studyCourse, user);

    const handleStudyClick = () => {
      if (isLocked && studyCourse) {
        setLockedStudyModal({ study, course: studyCourse });
      } else {
        onTakeStudy(study);
      }
    };

    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full group ${isLocked ? 'opacity-60' : ''}`}>
        <div
          className={`h-32 p-6 flex items-center justify-center relative overflow-hidden ${!study.coverImage ? 'bg-gradient-to-br from-primary to-blue-600' : 'bg-gray-900'}`}
          style={study.coverImage ? {
            backgroundImage: `url(${study.coverImage})`,
            backgroundSize: 'cover',
            backgroundPosition: study.coverImagePosition
              ? `${study.coverImagePosition.x}% ${study.coverImagePosition.y}%`
              : 'center'
          } : undefined}
        >
          <div className={`absolute inset-0 ${study.coverImage ? 'bg-black/50' : 'bg-black/10'}`}></div>
          {!study.coverImage && <Icons.BookOpen className="text-white/30 w-24 h-24 absolute -bottom-4 -right-4 transform rotate-12" />}

          {/* Lock Icon Overlay for locked studies */}
          {isLocked && (
            <div className="absolute top-3 right-3 z-20 bg-gray-900/80 rounded-full p-2">
              <Icons.Lock className="w-5 h-5 text-white" />
            </div>
          )}

          <h3 className="text-xl font-serif font-bold text-white relative z-10 text-center line-clamp-2 drop-shadow-md">{study.title}</h3>

          {/* Progress Bar Overlay */}
          {!isCommunityView && (
            <div className="absolute bottom-0 left-0 w-full h-1.5 bg-black/20 z-20">
              <div
                className={`h-full transition-all duration-500 ${isCompleted ? 'bg-green-400' : 'bg-yellow-400'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
        </div>
        <div className="p-6 flex-1 flex flex-col">
          {/* Cloned from Community Badge */}
          {study.clonedFrom && !isCommunityView && (
            <div className="mb-3 flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 px-2.5 py-1.5 rounded-lg border border-purple-200 w-fit">
              <Icons.Download className="w-3.5 h-3.5" />
              <span className="font-medium">From Community Library</span>
            </div>
          )}


          <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider justify-between">
            <span className="bg-gray-100 px-2 py-1 rounded">{study.segments.length} Segments</span>

            {!isCommunityView && (
              <>
                {isCompleted ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <Icons.Check className="w-3 h-3" /> Completed
                  </span>
                ) : progressPercent > 0 ? (
                  <span className="text-blue-600">{progressPercent}% Done</span>
                ) : null}
              </>
            )}

            {isCommunityView && (
              <span className="flex items-center gap-1 text-purple-600">
                <Icons.Globe className="w-3 h-3" /> Shared
              </span>
            )}

            {isAuthor && !isCommunityView && !study.clonedFrom && (
              <span className={`px-2 py-1 rounded ml-auto text-xs ${study.visibility === 'public' ? 'bg-blue-100 text-blue-700' :
                  study.visibility === 'all_orgs' ? 'bg-purple-100 text-purple-700' :
                    study.visibility === 'org_only' ? 'bg-green-100 text-green-700' :
                      'bg-yellow-100 text-yellow-700'
                }`}>
                {study.visibility === 'public' ? '🌍 Public' :
                  study.visibility === 'all_orgs' ? '🌐 All Orgs' :
                    study.visibility === 'org_only' ? '🏢 Org Only' :
                      '🔒 Private'}
              </span>
            )}

            {isAdoptedView && (
              <span className="px-2 py-1 rounded ml-auto bg-purple-100 text-purple-700 flex items-center gap-1">
                <Icons.Download className="w-3 h-3" /> Community
              </span>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">{study.description}</p>

          {study.tags && study.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {study.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200">
                  #{tag}
                </span>
              ))}
              {study.tags.length > 3 && (
                <span className="text-[10px] text-gray-400 py-0.5">+{study.tags.length - 3}</span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t mt-auto">
            <div className="text-xs text-gray-400">
              {study.clonedFrom && !isCommunityView ? (
                <div className="flex items-center gap-1">
                  <Icons.Download className="w-3 h-3 text-purple-500" />
                  <span className="text-purple-600 font-medium">Added from Community</span>
                  <span className="text-gray-400">by {formatAuthorName(study.authorName)}</span>
                </div>
              ) : (
                <span>By {formatAuthorName(study.authorName)}</span>
              )}
            </div>
            <div className="flex items-center gap-2">

              {/* Community View Actions */}
              {isCommunityView ? (
                <>
                  <button
                    onClick={() => setPreviewStudy(study)}
                    className="flex items-center gap-2 text-gray-600 font-semibold text-sm hover:bg-gray-100 px-3 py-1.5 rounded transition-colors"
                  >
                    <Icons.Eye className="w-4 h-4" /> Preview
                  </button>
                  {isAdopted ? (
                    <button
                      onClick={() => handleUnadoptStudy(study.id)}
                      className="flex items-center gap-2 text-red-600 font-semibold text-sm hover:bg-red-50 px-3 py-1.5 rounded transition-colors"
                    >
                      <Icons.Unadopt className="w-4 h-4" /> Remove
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAdoptStudy(study.id)}
                      className="flex items-center gap-2 bg-primary text-white font-semibold text-sm hover:bg-blue-900 px-4 py-1.5 rounded transition-colors shadow-sm"
                    >
                      <Icons.Download className="w-4 h-4" /> Add to Org
                    </button>
                  )}
                </>
              ) : (
                /* Standard View Actions */
                <>
                  {user.isAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditStudy(study);
                      }}
                      className="flex items-center gap-1 text-gray-500 hover:text-primary px-2 py-1 rounded transition-colors text-sm"
                    >
                      <Icons.Edit className="w-4 h-4" /> Edit
                    </button>
                  )}
                  <button
                    onClick={handleStudyClick}
                    className="flex items-center gap-2 text-primary font-semibold text-sm hover:underline"
                  >
                    {isCompleted ? 'Review' : (progressPercent > 0 ? 'Resume' : 'Start')} <Icons.Next className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show loading spinner until organization and initial data are ready
  if (loading || !organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.Loader className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-semibold">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab={activeTab}
        setActiveTab={(tab) => setActiveTab(tab as 'browse' | 'profile' | 'members' | 'courses')}
        onTabChange={() => {/* Already on dashboard, just change tab state */ }}
      />

      {/* Netflix-Style Hero for In-Progress Course or Course Discovery */}
      {(() => {
        if (activeTab !== 'browse') return null;

        // Find the first in-progress course (started but not completed)
        // Use allCourses which is loaded on mount, not courses which is only loaded on courses tab
        const inProgressCourse = allCourses.find(course => {
          if (!course.studyIds || course.studyIds.length === 0) return false;
          const completedInCourse = course.studyIds.filter(id => user.completedStudyIds?.includes(id)).length;
          return completedInCourse > 0 && completedInCourse < course.studyIds.length;
        });

        // If there's an in-progress course, show it
        if (inProgressCourse) {
          const completedCount = inProgressCourse.studyIds.filter(id => user.completedStudyIds?.includes(id)).length;
          const totalCount = inProgressCourse.studyIds.length;
          const progressPercent = Math.round((completedCount / totalCount) * 100);
          const nextStudyIndex = inProgressCourse.studyIds.findIndex(id => !user.completedStudyIds?.includes(id));
          const nextStudy = orgStudies.find(s => s.id === inProgressCourse.studyIds[nextStudyIndex]);

          return (
            <section className="relative overflow-hidden bg-gray-900">
              {/* Background Image */}
              <div className="absolute inset-0">
                {inProgressCourse.coverImage ? (
                  <img
                    src={inProgressCourse.coverImage}
                    alt=""
                    className="w-full h-full object-cover opacity-60"
                    style={{
                      objectPosition: inProgressCourse.coverImagePosition
                        ? `${inProgressCourse.coverImagePosition.x}% ${inProgressCourse.coverImagePosition.y}%`
                        : 'center'
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary via-blue-700 to-indigo-900" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-900/70 via-gray-900/50 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-gray-900/20" />
              </div>

              {/* Content */}
              <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 lg:py-16">
                <div className="max-w-2xl">
                  {/* Badge */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                      Continue Learning
                    </span>
                    <span className="text-white/60 text-sm">
                      {completedCount} of {totalCount} studies completed
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-3xl lg:text-4xl font-serif font-bold text-white mb-3 leading-tight">
                    {inProgressCourse.title}
                  </h2>

                  {/* Description */}
                  {inProgressCourse.description && (
                    <p className="text-white/70 text-lg mb-6 line-clamp-2">
                      {inProgressCourse.description}
                    </p>
                  )}

                  {/* Progress Bar */}
                  <div className="mb-6 max-w-md">
                    <div className="flex items-center justify-between text-sm text-white/70 mb-2">
                      <span>Your Progress</span>
                      <span className="font-bold text-white">{progressPercent}%</span>
                    </div>
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Next Study Preview */}
                  {nextStudy && (
                    <div className="mb-6 flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-3 max-w-md border border-white/10">
                      <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icons.Play className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-0.5">Up Next</p>
                        <p className="text-white font-semibold truncate">{nextStudy.title}</p>
                      </div>
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      onClick={() => navigate(`/course/${inProgressCourse.id}`)}
                      className="px-6 py-3 bg-white text-gray-900 font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-100 transition-all flex items-center gap-2 group"
                    >
                      <Icons.Play className="w-5 h-5" />
                      Continue Course
                      <Icons.ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      onClick={() => navigate(`/learn/${inProgressCourse.id}`)}
                      className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </section>
          );
        }

        // No in-progress course - show discovery carousel
        // Get courses that haven't been started (or are fully completed)
        const availableCourses = allCourses.filter(course => {
          if (!course.studyIds || course.studyIds.length === 0) return false;
          const completedInCourse = course.studyIds.filter(id => user.completedStudyIds?.includes(id)).length;
          // Show courses that are not started OR fully completed (for review)
          return completedInCourse === 0 || completedInCourse === course.studyIds.length;
        });

        if (availableCourses.length === 0) return null;

        const currentCourse = availableCourses[heroCarouselIndex % availableCourses.length];
        const isCompleted = user.completedCourseIds?.includes(currentCourse.id);
        const firstStudy = orgStudies.find(s => s.id === currentCourse.studyIds[0]);

        return (
          <section className="relative overflow-hidden bg-gray-900">
            {/* Background Image with Transition */}
            <div className="absolute inset-0 transition-opacity duration-700">
              {currentCourse.coverImage ? (
                <img
                  src={currentCourse.coverImage}
                  alt=""
                  className="w-full h-full object-cover opacity-60 transition-all duration-700"
                  style={{
                    objectPosition: currentCourse.coverImagePosition
                      ? `${currentCourse.coverImagePosition.x}% ${currentCourse.coverImagePosition.y}%`
                      : 'center'
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-600" />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/70 via-gray-900/50 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-gray-900/20" />
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 lg:py-16">
              <div className="max-w-2xl">
                {/* Badge */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                    {isCompleted ? 'Review Course' : 'Start a Course'}
                  </span>
                  <span className="text-white/60 text-sm">
                    {currentCourse.studyIds.length} studies
                  </span>
                </div>

                {/* Title */}
                <h2 className="text-3xl lg:text-4xl font-serif font-bold text-white mb-3 leading-tight">
                  {currentCourse.title}
                </h2>

                {/* Description */}
                {currentCourse.description && (
                  <p className="text-white/70 text-lg mb-6 line-clamp-2">
                    {currentCourse.description}
                  </p>
                )}

                {/* Tags */}
                {currentCourse.tags && currentCourse.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {currentCourse.tags.slice(0, 4).map((tag, i) => (
                      <span key={i} className="text-xs bg-white/10 text-white/80 px-3 py-1 rounded-full border border-white/20">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* First Study Preview */}
                {firstStudy && !isCompleted && (
                  <div className="mb-6 flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-3 max-w-md border border-white/10">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icons.BookOpen className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-0.5">Starts With</p>
                      <p className="text-white font-semibold truncate">{firstStudy.title}</p>
                    </div>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => navigate(`/course/${currentCourse.id}`)}
                    className="px-6 py-3 bg-white text-gray-900 font-bold rounded-xl shadow-lg hover:shadow-xl hover:bg-gray-100 transition-all flex items-center gap-2 group"
                  >
                    <Icons.Play className="w-5 h-5" />
                    {isCompleted ? 'Review Course' : 'Start Course'}
                    <Icons.ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => navigate(`/learn/${currentCourse.id}`)}
                    className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
                  >
                    View Details
                  </button>
                </div>

                {/* Carousel Indicators */}
                {availableCourses.length > 1 && (
                  <div className="flex items-center gap-3 mt-8">
                    <div className="flex gap-2">
                      {availableCourses.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setHeroCarouselIndex(i)}
                          className={`w-2.5 h-2.5 rounded-full transition-all ${i === heroCarouselIndex % availableCourses.length
                              ? 'bg-white w-8'
                              : 'bg-white/40 hover:bg-white/60'
                            }`}
                        />
                      ))}
                    </div>
                    <span className="text-white/50 text-sm ml-2">
                      {(heroCarouselIndex % availableCourses.length) + 1} of {availableCourses.length} courses
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Org Invite Banner for Admins - MOVED to Members Tab */}

      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'browse' && (
          <div className="space-y-6 animate-fade-in">
            <div className="space-y-4">
              <div>
                <h1 className="text-3xl font-serif font-bold text-gray-900">
                  Studies
                </h1>
                <p className="text-gray-500 mt-2">Explore bible studies created by your community.</p>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Icons.Search className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search studies..."
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:outline-none shadow-sm text-gray-700"
                />
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Sort By */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-500">Sort:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'recent' | 'oldest')}
                    className="px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:border-gray-300 focus:ring-2 focus:ring-primary focus:outline-none"
                  >
                    <option value="recent">Recently Added</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                </div>

                {/* Completion Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-500">Status:</span>
                  <select
                    value={completionFilter}
                    onChange={(e) => setCompletionFilter(e.target.value as 'all' | 'completed' | 'not-completed' | 'all-including-locked')}
                    className="px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:border-gray-300 focus:ring-2 focus:ring-primary focus:outline-none"
                  >
                    <option value="all">All Studies</option>
                    <option value="completed">Completed</option>
                    <option value="not-completed">Not Completed</option>
                    <option value="all-including-locked">All Including Locked</option>
                  </select>
                </div>

                {/* Clear All Filters */}
                {(selectedTags.length > 0 || completionFilter !== 'all' || sortBy !== 'recent') && (
                  <button
                    onClick={() => {
                      setSelectedTags([]);
                      setCompletionFilter('all');
                      setSortBy('recent');
                    }}
                    className="text-xs text-gray-500 hover:text-primary underline"
                  >
                    Reset All
                  </button>
                )}
              </div>

              {/* Tags Filter */}
              {uniqueTags.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <Icons.Filter className="w-4 h-4" /> Tags:
                  </span>
                  {uniqueTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all border ${selectedTags.includes(tag)
                          ? 'bg-primary text-white border-primary shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><Icons.Loader className="w-8 h-8 animate-spin text-primary" /></div>
            ) : (
              <>
                {filteredStudies.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300 mt-8">
                    <Icons.Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No studies found matching your criteria.</p>
                    <button onClick={() => { setSearchQuery(''); setSelectedTags([]); }} className="text-primary mt-2 font-medium hover:underline">Clear Search</button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {getPaginatedItems(filteredStudies, studiesPage).map(study => (
                        <StudyCard
                          key={study.id}
                          study={study}
                          isAuthor={!!(user.isAdmin && study.authorId === user.uid)}
                          isAdoptedView={organization?.adoptedStudyIds?.includes(study.id) || false}
                        />
                      ))}
                    </div>
                    <Pagination
                      currentPage={studiesPage}
                      totalPages={getTotalPages(filteredStudies.length)}
                      totalItems={filteredStudies.length}
                      itemsPerPage={ITEMS_PER_PAGE}
                      onPageChange={setStudiesPage}
                      itemLabel="studies"
                    />
                  </>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'members' && user.isAdmin && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">Organization Members</h1>
              <p className="text-gray-500">Manage user roles and permissions for {organization?.name}.</p>
            </div>

            {/* Invite Card */}
            {organization && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100 flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-lg font-bold text-blue-900 mb-1 flex items-center gap-2">
                    <Icons.Users className="w-5 h-5" /> Invite New Members
                  </h3>
                  <p className="text-blue-700 text-sm max-w-xl">
                    Share this link with your church members. When they click it, they'll be automatically added to your organization after signing up.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                  <div className="bg-white px-4 py-3 rounded-xl border border-blue-200 font-mono text-blue-800 font-bold tracking-wider select-all">
                    {organization.code}
                  </div>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/?orgCode=${organization.code}`;
                      navigator.clipboard.writeText(url);
                      alert("Invite link copied to clipboard!");
                    }}
                    className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-md flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <Icons.Share className="w-4 h-4" /> Copy Invite Link
                  </button>
                </div>
              </div>
            )}

            {loadingMembers ? (
              <div className="flex justify-center py-20"><Icons.Loader className="w-8 h-8 animate-spin text-primary" /></div>
            ) : (
              <>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">User</th>
                      <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Role</th>
                      <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Joined</th>
                      <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {getPaginatedItems(members, membersPage).map(member => (
                      <tr key={member.uid} className="hover:bg-gray-50/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center text-blue-900 font-bold">
                              {member.displayName ? member.displayName[0] : 'U'}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{member.displayName || 'Unknown User'}</div>
                              <div className="text-sm text-gray-400">{member.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${member.isAdmin ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                            {member.isAdmin ? <Icons.Shield className="w-3 h-3" /> : <Icons.User className="w-3 h-3" />}
                            {member.isAdmin ? 'Admin' : 'Member'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {/* Assuming we might store join date later, for now placeholder or generic */}
                          --
                        </td>
                        <td className="px-6 py-4 text-right">
                          {/* Prevent editing own role or organization owner if implemented */}
                          {member.uid !== user.uid && (
                            member.isAdmin ? (
                              <button
                                onClick={() => handleUpdateRole(member.uid, false)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1 ml-auto"
                              >
                                <Icons.ShieldOff className="w-4 h-4" /> Demote
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateRole(member.uid, true)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 ml-auto"
                              >
                                <Icons.Shield className="w-4 h-4" /> Promote
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {members.length === 0 && (
                  <div className="p-12 text-center text-gray-500">No members found in this organization.</div>
                )}
              </div>
              <Pagination
                currentPage={membersPage}
                totalPages={getTotalPages(members.length)}
                totalItems={members.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setMembersPage}
                itemLabel="members"
              />
              </>
            )}
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="space-y-6 animate-fade-in">
            <div className="space-y-4">
              <div>
                <h1 className="text-3xl font-serif font-bold text-gray-900">Courses</h1>
                <p className="text-gray-500 mt-1">Curated learning paths with sequential studies</p>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icons.BookOpen className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No courses yet</h3>
                <p className="text-gray-500 mb-6">Check back later for curated learning paths</p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getPaginatedItems(courses, coursesPage).map(course => {
                    const completedCount = course.studyIds?.filter(id => user.completedStudyIds?.includes(id))?.length || 0;
                    const progress = (course.studyIds?.length || 0) > 0 ? (completedCount / (course.studyIds?.length || 1)) * 100 : 0;
                    const isCompleted = user.completedCourseIds?.includes(course.id);
                    const metrics = courseMetrics[course.id];

                    return (
                      <div key={course.id} className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all border border-gray-100 overflow-hidden">
                        {/* Cover Image or Gradient */}
                        {course.coverImage ? (
                          <div
                            className="h-40 bg-cover bg-center"
                            style={{
                              backgroundImage: `url(${course.coverImage})`,
                              backgroundPosition: `${course.coverImagePosition?.x || 50}% ${course.coverImagePosition?.y || 50}%`
                            }}
                          >
                            <div className="w-full h-full bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                              <h3 className="text-white font-bold text-lg line-clamp-2">{course.title}</h3>
                            </div>
                          </div>
                        ) : (
                          <div className="h-40 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-end p-4">
                            <h3 className="text-white font-bold text-lg line-clamp-2">{course.title}</h3>
                          </div>
                        )}

                        {/* Content */}
                        <div className="p-5">
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.description}</p>

                          {/* Stats */}
                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center gap-1">
                              <Icons.BookOpen className="w-4 h-4" />
                              {course.studyIds?.length || 0} studies
                            </span>
                            {isCompleted && (
                              <span className="flex items-center gap-1 text-green-600 font-medium">
                                <Icons.Check className="w-4 h-4" />
                                Completed
                              </span>
                            )}
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-4">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                              <span>{completedCount} of {course.studyIds?.length || 0} completed</span>
                              <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${isCompleted ? 'bg-green-500' : 'bg-blue-500'} transition-all`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>

                          {/* Tags */}
                          {course.tags && course.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {course.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                                  {tag}
                                </span>
                              ))}
                              {course.tags.length > 3 && (
                                <span className="text-xs text-gray-500">+{course.tags.length - 3}</span>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <button
                            onClick={() => onViewCourse(course.id)}
                            className="w-full py-2 bg-primary text-white rounded-lg font-medium hover:bg-blue-900 transition-colors text-sm"
                          >
                            {completedCount > 0 && !isCompleted ? 'Continue' : isCompleted ? 'Review' : 'Start'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Pagination
                  currentPage={coursesPage}
                  totalPages={getTotalPages(courses.length)}
                  totalItems={courses.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCoursesPage}
                  itemLabel="courses"
                />
              </>
            )}
          </div>
        )}

        {/* Delete Course Confirmation Modal */}
        {courseToDelete && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
            <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4 animate-scale-up">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.Trash className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">Delete Course?</h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to delete "<strong>{courseToDelete.title}</strong>"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setCourseToDelete(null)}
                  className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await deleteCourse(courseToDelete.id);
                    setCourseToDelete(null);
                    setActiveTab('courses'); // Refresh the tab
                  }}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
            {/* Profile Header */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 bg-secondary rounded-full flex items-center justify-center text-3xl font-bold text-blue-900 border-4 border-white shadow-lg shrink-0">
                {user.displayName ? user.displayName[0] : 'U'}
              </div>
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl font-serif font-bold text-gray-900">{user.displayName || 'Pilgrim'}</h1>
                <div className="text-gray-500 mb-2">{organization?.name}</div>
                <div className="flex flex-col md:flex-row items-center gap-4 mt-2 text-gray-500 justify-center md:justify-start">
                  <span className="flex items-center gap-1"><Icons.Star className="w-4 h-4 text-yellow-500" /> {user.xp ?? 0} XP</span>
                  <span className="flex items-center gap-1"><Icons.Check className="w-4 h-4 text-green-500" /> {user.completedStudyIds?.length || 0} Studies Completed</span>
                  {user.currentStreak !== undefined && user.currentStreak > 0 && (
                    <span className="flex items-center gap-1">
                      <Icons.Flame className="w-4 h-4 text-orange-500" />
                      <span className="font-bold text-orange-600">{user.currentStreak} Day Streak</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Completed Studies */}
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Icons.Check className="w-5 h-5 text-green-500" />
                Completed Studies ({user.completedStudyIds?.length || 0})
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                {(user.completedStudyIds?.length || 0) === 0 ? (
                  <div className="p-12 text-center text-gray-400 italic">
                    <Icons.BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p>You haven't completed any studies yet.</p>
                    <p className="text-sm mt-2">Start learning to see your progress here!</p>
                  </div>
                ) : (
                  <>
                  <div className="divide-y divide-gray-100">
                    {getPaginatedItems(
                      orgStudies.filter(study => user.completedStudyIds?.includes(study.id) || false),
                      completedStudiesPage
                    ).map(study => {
                        const completedDate = new Date(study.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });

                        return (
                          <div
                            key={study.id}
                            className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4 cursor-pointer"
                            onClick={() => onTakeStudy(study)}
                          >
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                              <Icons.Check className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 truncate">{study.title}</h3>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Icons.BookOpen className="w-3 h-3" />
                                  {study.segments.length} segments
                                </span>
                                {study.clonedFrom && (
                                  <span className="flex items-center gap-1 text-purple-600">
                                    <Icons.Download className="w-3 h-3" />
                                    Community
                                  </span>
                                )}
                                <span>By {formatAuthorName(study.authorName)}</span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onTakeStudy(study);
                              }}
                              className="text-primary font-semibold text-sm hover:underline flex items-center gap-1"
                            >
                              Review <Icons.Next className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                  </div>
                  <div className="px-4 pb-2">
                    <Pagination
                      currentPage={completedStudiesPage}
                      totalPages={getTotalPages(orgStudies.filter(s => user.completedStudyIds?.includes(s.id)).length)}
                      totalItems={orgStudies.filter(s => user.completedStudyIds?.includes(s.id)).length}
                      itemsPerPage={ITEMS_PER_PAGE}
                      onPageChange={setCompletedStudiesPage}
                      itemLabel="studies"
                    />
                  </div>
                  </>
                )}
              </div>
            </div>

            {/* Awards Grid */}
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Icons.Award className="w-5 h-5 text-secondary" />
                Awards & Achievements
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(user.awards?.length ?? 0) === 0 ? (
                  <div className="col-span-full p-8 text-center bg-gray-50 rounded-lg text-gray-400 italic">
                    Complete studies to earn awards!
                  </div>
                ) : (
                  user.awards?.map((award, i) => (
                    <div key={i} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center text-center">
                      <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3 text-primary">
                        <Icons.Award className="w-6 h-6" />
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">{award.name}</h3>
                      <p className="text-xs text-gray-500 mt-1">{award.description}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* My Questions Section */}
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Icons.MessageSquare className="w-5 h-5 text-primary" />
                My Questions ({userQuestions.length})
              </h2>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                {loadingQuestions ? (
                  <div className="p-12 text-center">
                    <Icons.Loader className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">Loading your questions...</p>
                  </div>
                ) : userQuestions.length === 0 ? (
                  <div className="p-12 text-center text-gray-400 italic">
                    <Icons.MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p>You haven't asked any questions yet.</p>
                    <p className="text-sm mt-2">Look for "question" segments in studies to ask the author!</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {userQuestions.map((question) => {
                      const study = orgStudies.find(s => s.id === question.studyId);
                      const hasReplies = question.replies && question.replies.length > 0;

                      return (
                        <div
                          key={question.id}
                          className={`p-4 hover:bg-gray-50 transition-colors ${!hasReplies ? 'border-l-4 border-orange-400' : ''}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${hasReplies ? 'bg-green-100' : 'bg-orange-100'}`}>
                              {hasReplies ? (
                                <Icons.Check className="w-5 h-5 text-green-600" />
                              ) : (
                                <Icons.Clock className="w-5 h-5 text-orange-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-gray-900">
                                  {study ? study.title : 'Unknown Study'}
                                </h3>
                                {!hasReplies && (
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                    Pending
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-700 mb-2">{question.questionText}</p>
                              <p className="text-xs text-gray-500 mb-3">
                                Asked on {new Date(question.createdAt).toLocaleDateString()}
                              </p>

                              {/* Replies */}
                              {hasReplies && (
                                <div className="mt-3 pl-4 border-l-2 border-green-500 space-y-2">
                                  {question.replies?.map((reply) => (
                                    <div key={reply.id} className="bg-green-50 rounded-lg p-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold text-green-900">
                                          {formatAuthorName(reply.authorName)} (Author)
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {new Date(reply.createdAt).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-800">{reply.replyText}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* View Study Button */}
                              {study && (
                                <button
                                  onClick={() => onTakeStudy(study)}
                                  className="mt-3 text-primary font-semibold text-sm hover:underline flex items-center gap-1"
                                >
                                  View Study <Icons.Next className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {studyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl transform scale-100 animate-bounce-in">
            <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Delete Study?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{studyToDelete.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setStudyToDelete(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeDeleteStudy}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Study Preview Modal */}
      {previewStudy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] shadow-2xl transform scale-100 animate-bounce-in overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">{previewStudy.title}</h2>
                <p className="text-gray-600 text-sm mb-3">{previewStudy.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Icons.User className="w-4 h-4" /> {formatAuthorName(previewStudy.authorName)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icons.BookOpen className="w-4 h-4" /> {previewStudy.segments.length} Segments
                  </span>
                  {previewStudy.tags && previewStudy.tags.length > 0 && (
                    <div className="flex gap-1">
                      {previewStudy.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setPreviewStudy(null)}
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Study Content</h3>
              <div className="space-y-4">
                {previewStudy.segments.map((segment, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                        Segment {index + 1}
                      </span>
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        {segment.type}
                      </span>
                    </div>

                    {segment.type === 'text' && (
                      <p className="text-gray-700 whitespace-pre-wrap">{segment.content}</p>
                    )}

                    {segment.type === 'verse' && (
                      <div className="mt-2 bg-blue-50 border-l-4 border-blue-500 p-3">
                        <p className="text-gray-800 italic">{segment.content}</p>
                        {segment.metadata?.book && segment.metadata?.chapter && segment.metadata?.verses && (
                          <p className="text-sm text-blue-700 font-semibold mt-2">
                            — {segment.metadata.book} {segment.metadata.chapter}:{segment.metadata.verses} ({segment.metadata.version || 'NIV'})
                          </p>
                        )}
                      </div>
                    )}

                    {segment.type === 'image' && (
                      <div className="mt-2">
                        <img
                          src={segment.content}
                          alt={`Segment ${index + 1}`}
                          className="max-w-full h-auto rounded border border-gray-300"
                        />
                        {segment.metadata?.caption && (
                          <p className="text-sm text-gray-600 mt-2">{segment.metadata.caption}</p>
                        )}
                      </div>
                    )}

                    {segment.type === 'video' && (
                      <div className="mt-2">
                        <div className="bg-gray-200 rounded p-3 flex items-center gap-2">
                          <Icons.Youtube className="w-5 h-5 text-red-600" />
                          <span className="text-sm text-gray-700 font-mono">{segment.content}</span>
                        </div>
                      </div>
                    )}

                    {segment.type === 'quiz' && segment.metadata?.quizOptions && (
                      <div className="mt-2 space-y-2">
                        <p className="font-semibold text-gray-800">{segment.content}</p>
                        <div className="space-y-1">
                          {segment.metadata.quizOptions.map((option: string, i: number) => (
                            <div
                              key={i}
                              className={`p-2 rounded text-sm ${i === segment.metadata?.correctAnswer
                                  ? 'bg-green-100 text-green-800 font-medium'
                                  : 'bg-white text-gray-700 border border-gray-200'
                                }`}
                            >
                              {option} {i === segment.metadata?.correctAnswer && '✓'}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setPreviewStudy(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              {!orgStudies.some(study => study.clonedFrom === previewStudy.id) && (
                <button
                  onClick={() => {
                    handleAdoptStudy(previewStudy.id);
                    setPreviewStudy(null);
                  }}
                  className="flex-1 px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-blue-900 transition-colors shadow-md flex items-center justify-center gap-2"
                >
                  <Icons.Download className="w-4 h-4" /> Add to My Organization
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Locked Study Modal */}
      {lockedStudyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform scale-100 animate-bounce-in overflow-hidden">
            {/* Header with Lock Icon */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <Icons.Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-serif font-bold text-gray-900">Study Locked</h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-600 text-center mb-4">
                <strong>"{lockedStudyModal.study.title}"</strong> is part of a sequential course and must be completed in order.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800 mb-2">This study is part of:</p>
                <p className="font-semibold text-blue-900 flex items-center gap-2">
                  <Icons.BookOpen className="w-4 h-4" />
                  {lockedStudyModal.course.title}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Complete the earlier studies in this course to unlock it.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setLockedStudyModal(null)}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    navigate(`/course/${lockedStudyModal.course.id}`);
                    setLockedStudyModal(null);
                  }}
                  className="flex-1 px-4 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors shadow-md flex items-center justify-center gap-2"
                >
                  <Icons.Play className="w-4 h-4" />
                  Go to Course
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
